import { Button } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";
import { EmailAccountMetadata, useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { CRMStatusPill } from "../../../../components/CRMStatusPill";
import { DomainRecordsTable } from "./components/DomainRecordsTable";
import { ReactComponent as RefreshIcon } from "./assets/refresh.svg";
import { ReactComponent as InfoBlackIcon } from "./assets/info-black.svg";

export const AuthenticateDomainStep: React.FC<{
  metadata: EmailAccountMetadata;
}> = ({ metadata }) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const [getEmailConfig, { isFetching: isEmailConfigFetching }] =
    integrationsApi.useLazyGetEmailConfigQuery();

  const domainRecords = metadata.domain_records ?? [];

  const setupSteps = useMemo(
    () => [
      {
        id: "go-to-domain-settings",
        text: t({
          id: "client-support-page.domain-auth-step-go-to-domain-settings",
          defaultMessage: "Go to your Domain provider's general settings",
        }),
      },
      {
        id: "open-dns-settings",
        text: t({
          id: "client-support-page.domain-auth-step-open-dns-settings",
          defaultMessage: "Open DNS settings",
        }),
      },
      {
        id: "copy-domain-records",
        text: t({
          id: "client-support-page.domain-auth-step-copy-domain-records",
          defaultMessage:
            "Copy each of the domain record below, paste it into DNS settings and save",
        }),
      },
    ],
    [t]
  );

  return (
    <div className="flex flex-col gap-10">
      <div className="flex max-w-150 gap-4">
        <div className="w-px shrink-0 self-stretch rounded-[1px] bg-[#E0E0E0]" />
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-1">
            <span className="font-secondary text-lg font-medium text-dark">
              {t({
                id: "client-support-page.authenticate-your-domain",
                defaultMessage: "Authenticate your domain",
              })}
            </span>
            <span className="font-secondary text-[13px] leading-[150%] text-dark-700">
              {t({
                id: "client-support-page.authenticate-your-domain-description",
                defaultMessage:
                  "If you want replies to be sent from your own email address, instead of Jugl Inbox email, authenticate your domain with DNS",
              })}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <span className="font-secondary text-[13px] font-medium text-dark">
              {t({
                id: "client-support-page.how-to-set-it-up",
                defaultMessage: "ℹ️ How to set it up:",
              })}
            </span>

            <div className="flex flex-col gap-1">
              {setupSteps.map((step, index) => (
                <div key={step.id} className="flex items-center gap-2">
                  <span className="flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full bg-[#2F80ED] font-secondary text-[10px] font-semibold text-white">
                    {index + 1}
                  </span>
                  <span className="font-secondary text-[13px] leading-[160%] text-dark-700">
                    {step.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="ml-2 flex items-center gap-3.5">
          <span className="font-secondary font-medium text-dark">
            {t({
              id: "client-support-page.domain-records",
              defaultMessage: "Domain records",
            })}
          </span>
          <CRMStatusPill
            status={metadata.domain_auth_verified ? "verified" : "pending"}
            size="sm"
          />
        </div>

        {!metadata.domain_auth_verified && (
          <div className="flex w-max items-center gap-3 rounded-xl bg-grey-100 p-3">
            <InfoBlackIcon />
            <span className="font-secondary text-[13px] leading-[140%] text-dark">
              {t({
                id: "client-support-page.domain-auth-status-info",
                defaultMessage:
                  "This may take a few hours, depends on your Domain Provider",
              })}
            </span>
          </div>
        )}

        {domainRecords.length > 0 && (
          <DomainRecordsTable records={domainRecords} />
        )}
      </div>
      <Button
        variant="secondary"
        iconStart={<RefreshIcon />}
        isDisabled={isEmailConfigFetching}
        onClick={() => getEmailConfig({ entityId })}
        className="w-70"
      >
        {t({
          id: "common.refresh",
          defaultMessage: "Refresh",
        })}
      </Button>
    </div>
  );
};
